import IndexedDBService from './indexedDBService';

/**
 * Offline Database Service
 * Manages offline data caching for all Firestore collections
 */
class OfflineDB extends IndexedDBService {
  private readonly DB_NAME = 'offlineDB';
  private readonly VERSION = 8; // Incremented to add mevTransactionQueue collection

  // Collections that are considered transaction data
  private readonly TRANSACTION_COLLECTIONS = [
    'orders',
    'sessions',
    'transactions',
    'activeSessions',
  ];

  // Master data collections that need dedicated stores for performance
  private readonly MASTER_DATA_COLLECTIONS = [
    'stores',
    'items',
    'menus', // New menu system (multiple menus per store)
    'categories', // New category system (categories linked to menus)
    'topMenus',
    'taxes',
    'customFields',
    'advancedOption', // Advanced option sets
    'itemTags',
    'printers',
    'tables',
    'staffs',
    'customers',
    'suppliers',
    'ingredients',
    'recipes',
    'promotions',
    'giftCards',
    'loyaltyPrograms',
    'paymentMethods',
    'deliveryZones',
  ];

  constructor() {
    super('offlineDB', 8); // Incremented to match VERSION
  }

  protected setupObjectStores(db: IDBDatabase): void {
    // Master Data Stores - Dedicated stores for each collection for performance
    this.MASTER_DATA_COLLECTIONS.forEach(collection => {
      if (!db.objectStoreNames.contains(collection)) {
        const store = db.createObjectStore(collection, { keyPath: 'id' });
        store.createIndex('storeId', 'storeId', { unique: false });
        store.createIndex('updated', 'updated', { unique: false });
      }
    });
    // Dynamic Collections Store - stores all Firestore documents
    if (!db.objectStoreNames.contains('collections')) {
      const collectionsStore = db.createObjectStore('collections', {
        keyPath: ['collectionName', 'docId'],
      });
      collectionsStore.createIndex('collectionName', 'collectionName', {
        unique: false,
      });
      collectionsStore.createIndex('docId', 'docId', { unique: false });
      collectionsStore.createIndex('synced', 'synced', { unique: false });
      collectionsStore.createIndex('lastSync', 'lastSync', { unique: false });
    }

    // Images Store - stores image blobs locally
    if (!db.objectStoreNames.contains('images')) {
      const imagesStore = db.createObjectStore('images', {
        keyPath: 'imageId',
      });
      imagesStore.createIndex('storeId', 'storeId', { unique: false });
      imagesStore.createIndex('synced', 'synced', { unique: false });
      imagesStore.createIndex('lastSync', 'lastSync', { unique: false });
    }

    // Sync Queue Store - tracks pending sync operations
    if (!db.objectStoreNames.contains('syncQueue')) {
      const syncQueueStore = db.createObjectStore('syncQueue', {
        keyPath: 'id',
        autoIncrement: true,
      });
      syncQueueStore.createIndex('dataType', 'dataType', { unique: false });
      syncQueueStore.createIndex('collection', 'collection', { unique: false });
      syncQueueStore.createIndex('synced', 'synced', { unique: false });
      syncQueueStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Storage Queue Store - tracks pending Firebase Storage operations
    if (!db.objectStoreNames.contains('storageQueue')) {
      const storageQueueStore = db.createObjectStore('storageQueue', {
        keyPath: 'id',
        autoIncrement: true,
      });
      storageQueueStore.createIndex('operation', 'operation', {
        unique: false,
      });
      storageQueueStore.createIndex('synced', 'synced', { unique: false });
      storageQueueStore.createIndex('lastSync', 'lastSync', { unique: false });
    }

    // Image Cache Store - caches images by Firebase Storage URL
    if (!db.objectStoreNames.contains('imageCache')) {
      const imageCacheStore = db.createObjectStore('imageCache', {
        keyPath: 'id',
      });
      imageCacheStore.createIndex('cachedAt', 'cachedAt', { unique: false });
    }

    // MEV Certificates Store - stores certificate metadata and keys
    if (!db.objectStoreNames.contains('mevCertificates')) {
      const mevCertStore = db.createObjectStore('mevCertificates', {
        keyPath: 'key',
      });
      mevCertStore.createIndex('storeId', 'storeId', { unique: false });
      mevCertStore.createIndex('env', 'env', { unique: false });
      mevCertStore.createIndex('type', 'type', { unique: false });
      mevCertStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // MEV Transaction Queue Store - stores unsent MEV transactions for batch sync
    if (!db.objectStoreNames.contains('mevTransactionQueue')) {
      const mevQueueStore = db.createObjectStore('mevTransactionQueue', {
        keyPath: 'id',
        autoIncrement: true,
      });
      mevQueueStore.createIndex('status', 'status', { unique: false });
      mevQueueStore.createIndex('createdAt', 'createdAt', { unique: false });
      mevQueueStore.createIndex('datTrans', 'metadata.datTrans', { unique: false });
    }
  }

  /**
   * Detect if collection is transaction data
   */
  isTransactionData(collectionName: string): boolean {
    return this.TRANSACTION_COLLECTIONS.includes(collectionName);
  }

  /**
   * Detect if collection is master data
   */
  isMasterData(collectionName: string): boolean {
    return !this.isTransactionData(collectionName);
  }

  /**
   * Save document to local cache
   */
  async saveDocument(
    collectionName: string,
    docId: string,
    data: any,
    synced: boolean = false
  ): Promise<void> {
    await this.put('collections', {
      collectionName,
      docId,
      data,
      synced,
      lastSync: synced ? Date.now() : null,
    });
  }

  /**
   * Get document from local cache
   */
  async getDocument(collectionName: string, docId: string): Promise<any> {
    const doc = await this.get('collections', [collectionName, docId]);
    return doc?.data || null;
  }

  /**
   * Get all documents for a collection
   */
  async getCollection(collectionName: string): Promise<any[]> {
    const docs = await this.queryByIndex(
      'collections',
      'collectionName',
      collectionName
    );
    return docs.map((doc) => ({ id: doc.docId, ...doc.data }));
  }

  /**
   * Delete document from local cache
   */
  async deleteDocument(collectionName: string, docId: string): Promise<void> {
    await this.delete('collections', [collectionName, docId]);
  }

  /**
   * Mark document as synced
   */
  async markSynced(collectionName: string, docId: string): Promise<void> {
    const doc = await this.get('collections', [collectionName, docId]);
    if (doc) {
      await this.put('collections', {
        ...doc,
        synced: true,
        lastSync: Date.now(),
      });
    }
  }

  /**
   * Get unsynced documents
   */
  async getUnsyncedDocuments(collectionName?: string): Promise<any[]> {
    const unsynced = await this.queryByIndex('collections', 'synced', false);

    if (collectionName) {
      return unsynced.filter((doc) => doc.collectionName === collectionName);
    }

    return unsynced;
  }

  /**
   * Save image blob locally
   */
  async saveImage(
    imageId: string,
    storeId: string,
    blob: Blob,
    url?: string,
    synced: boolean = false
  ): Promise<void> {
    // Convert blob to ArrayBuffer for storage
    const arrayBuffer = await blob.arrayBuffer();

    await this.put('images', {
      imageId,
      storeId,
      blob: arrayBuffer,
      url,
      synced,
      lastSync: synced ? Date.now() : null,
    });
  }

  /**
   * Get image blob from local storage
   */
  async getImage(imageId: string): Promise<Blob | null> {
    const image = await this.get('images', imageId);
    if (!image || !image.blob) {
      return null;
    }
    return new Blob([image.blob]);
  }

  /**
   * Save master data document to IndexedDB
   */
  async saveMasterData(collection: string, docId: string, data: any): Promise<void> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    await this.put(collection, {
      ...data,
      id: docId,
    });
  }

  /**
   * Get master data document from IndexedDB
   */
  async getMasterData(collection: string, docId: string): Promise<any> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    return await this.get(collection, docId);
  }

  /**
   * Get all master data documents for a collection
   */
  async getAllMasterData(collection: string): Promise<any[]> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    return await this.getAll(collection);
  }

  /**
   * Get master data filtered by storeId
   */
  async getMasterDataByStore(collection: string, storeId: string): Promise<any[]> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    return await this.queryByIndex(collection, 'storeId', storeId);
  }

  /**
   * Delete master data document from IndexedDB
   */
  async deleteMasterData(collection: string, docId: string): Promise<void> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    await this.delete(collection, docId);
  }

  /**
   * Clear all documents in a master data collection
   */
  async clearMasterDataCollection(collection: string): Promise<void> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    const allDocs = await this.getAll(collection);
    for (const doc of allDocs) {
      await this.delete(collection, doc.id);
    }
  }

  /**
   * Bulk save master data documents
   */
  async bulkSaveMasterData(collection: string, docs: any[]): Promise<void> {
    if (!this.MASTER_DATA_COLLECTIONS.includes(collection)) {
      throw new Error(`Unknown master data collection: ${collection}`);
    }

    for (const doc of docs) {
      await this.put(collection, doc);
    }
  }

  /**
   * Check if a collection is a master data collection
   */
  isMasterDataCollection(collection: string): boolean {
    return this.MASTER_DATA_COLLECTIONS.includes(collection);
  }
}

// Singleton instance
const offlineDB = new OfflineDB();

export default offlineDB;

